import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  handleUnauthorized,
  showConfirmationDialog,
} from "../../utils/apiHelper";
import moment from "moment";
import { getModulePermissions } from "../../utils/permissionUtils";

const AdminReceivedNotifications = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterRead, setFilterRead] = useState("all"); // all, read, unread

  const authToken = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const { shouldCheckPermissions, canAdd, canView, canDelete, canEdit } =
    getModulePermissions("mynotification");

  useEffect(() => {
    fetchData(currentPage);
    // eslint-disable-next-line
  }, [
    currentPage,
    searchQuery,
    itemsPerPage,
    sortColumn,
    sortOrder,
    filterRead,
  ]);

  const fetchData = async (page) => {
    setLoading(true);
    try {
      const searchParam =
        filterRead === "read"
          ? "read"
          : filterRead === "unread"
            ? "unread"
            : searchQuery;

      const params = new URLSearchParams({
        page,
        limit: itemsPerPage,
        sort: sortColumn,
        order: sortOrder,
        search: searchParam,
      }).toString();

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/adminReceiveNotification?${params}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "Failed to fetch data");

      setItems(data.notifications || []);
      setTotalItems(data.metadata?.totalNotifications || 0);
      setTotalPages(data.metadata?.totalPages || 1);
    } catch (err) {
      setItems([]);
      setErrorMessage(err.message);
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column)
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedItems(items.map((item) => item._id));
    else setSelectedItems([]);
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const isSelected = (id) => selectedItems.includes(id);

  const handleDeleteAll = async (id, single = "No") => {
    const idsToDelete = single === "Yes" ? [id] : selectedItems;
    if (idsToDelete.length === 0) return;

    const result = await showConfirmationDialog(
      `This action will delete the notification!`,
      `Yes, delete it!`
    );
    if (!result.isConfirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/deleteNotificationAdmin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ id: idsToDelete, type: "received" }),
        }
      );

      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to delete");

      setSuccessMessage("Notification deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);

      if (single === "No") setSelectedItems([]);
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err.message);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/updateNotificationStatus/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ isRead: currentStatus === "1" ? "0" : "1" }),
        }
      );

      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      setSuccessMessage("Status updated!");
      setTimeout(() => setSuccessMessage(""), 3000);

      // 🔄 refresh list
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err.message);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleReadAll = async () => {
    const result = await showConfirmationDialog(
      `Mark ALL notifications as Read?`,
      `Yes, mark all as read`
    );

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/readAllNotifications`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      setSuccessMessage("All notifications marked as read!");
      setSelectedItems([]);
      setTimeout(() => setSuccessMessage(""), 3000);

      // Refresh table
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err.message);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleNext = () => handlePageChange(currentPage + 1);
  const handlePrev = () => handlePageChange(currentPage - 1);

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div className="d-flex align-items-center gap-3">
          <span>Show</span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} items per page
              </option>
            ))}
          </select>

          <form className="navbar-search position-relative">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Icon
              icon="ion:search-outline"
              className="icon position-absolute"
            />
            {searchQuery && (
              <button
                type="button"
                className="position-absolute end-0"
                onClick={() => setSearchQuery("")}

                style={{
                  position: "absolute",
                  insetInlineEnd: "1.9375rem",
                  top: "40%",
                  transform: "translateY(-50%)",
                  fontSize: "1.125rem",
                  color: "var(--black)", // Corrected from `color: var(--black)` which was outside the object
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}

              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          <select
            className="form-select w-auto"
            value={filterRead}
            onChange={(e) => setFilterRead(e.target.value)}
          >
            <option value="all">All</option>
            <option value="read">Read</option>
            <option value="unread">Unread</option>
          </select>
          {(!shouldCheckPermissions || canEdit) && (
            <button
              className="btn btn-primary"
              onClick={handleReadAll}
              disabled={selectedItems.length === 0}
            >
              Mark All Read
            </button>
          )}
          {(!shouldCheckPermissions || canDelete) && (
            <button
              className="btn btn-danger"
              onClick={() => handleDeleteAll(null, "No")}
              disabled={selectedItems.length === 0}
            >
              Delete All
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th style={{ width: "50px" }}>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={
                        selectedItems.length === items.length &&
                        items.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </div>
                </th>

                <th
                  onClick={() => handleSort("title")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    Title
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "title" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "title" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>

                <th>Status</th>

                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-10">
                    Date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>

                <th style={{ width: "110px" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isSelected(item._id)}
                          onChange={() => handleSelectItem(item._id)}
                        />
                      </div>
                    </td>

                    <td>{item.title || "N/A"}</td>

                    <td>
                      {!shouldCheckPermissions || canEdit ? (
                        // ✅ Clickable when user has edit permission
                        <span
                          onClick={() =>
                            handleToggleStatus(item._id, item.isRead)
                          }
                          className={`px-24 py-4 radius-4 fw-medium text-sm border ${item.isRead === "1"
                            ? "bg-success-focus text-success-600 border-success-main"
                            : "bg-warning-focus text-warning-600 border-warning-main"
                            }`}
                          style={{ cursor: "pointer" }}
                        >
                          {item.isRead === "1" ? "Mark Unread" : "Mark Read"}
                        </span>
                      ) : (
                        // 🚫 Non-clickable when user has no edit permission
                        <span
                          className={`px-24 py-4 radius-4 fw-medium text-sm border ${item.isRead === "1"
                            ? "bg-success-focus text-success-600 border-success-main opacity-50"
                            : "bg-warning-focus text-warning-600 border-warning-main opacity-50"
                            }`}
                          style={{ cursor: "default" }}
                          title="You don’t have permission to change status"
                        >
                          {item.isRead === "1" ? "Mark Unread" : "Mark Read"}
                        </span>
                      )}
                    </td>

                    <td>{moment(item.createdAt).format("DD-MM-YYYY")}</td>

                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {(!shouldCheckPermissions || canView) && (
                          <button
                            title="View"
                            onClick={() =>
                              navigate(
                                `/mynotification/view/received/${item._id}`
                              )
                            }
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="majesticons:eye-line" className="icon text-xl" />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canDelete) && (
                          <button
                            title="Delete"
                            onClick={() => handleDeleteAll(item._id, "Yes")}
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            entries
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handlePrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>
            {getPaginationRange().map((page) => (
              <li key={page} className="page-item">
                <Link
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                    ? "bg-primary-600 text-white"
                    : "bg-neutral-200"
                    }`}
                  to="#"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </Link>
              </li>
            ))}
            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handleNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminReceivedNotifications;
